// The controller that uses group_api service to get the data from the API and dispatch the actions to the reducer
import { createSelector } from '@reduxjs/toolkit';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { removeInvitationFromCompanyAdminGroup, setCompanyAdminGroup } from './companySlice';
import { setGroup, clearGroup, removeMember, removeInvitation, setCurrentAdminGroup, type pGroup } from './groupSlice';
import { addAdminInLib, removeGroup, setGroups } from './groupsSlice';
import { updatePendingGroupConfigHoldingProps } from './holdingConfigSlice';
import { setMembers } from './membersSlice';
import {
    type PendingGroupSlice,
    clearPendingGroup,
    setPendingGroup,
    updatePendingGroupProps,
} from './pendingGroupSlice';
import { setApiResponseMeta } from '../../common/application/apiResponseMetaSlice';
import { setApiResponseStatus } from '../../common/application/apiResponseStatusSlice';
import { handleHttpError } from '../../common/application/handleHttpError';
import { toggleToast } from '../../common/application/toastSlice';
import useGroupApi from '../data/groupApi';
import { useGroupStore } from '../data/groupStore';
import { EnumRelation, fleetRelationTargetIds, type Group, type RelatesTo } from '../domain/group';
import type { GroupConfigEnterprise, GroupConfigOf } from '../domain/groupConfig';
import type { PostInvitation, Member } from '../domain/member';
import { defaultPacketConfig, type Packet } from '../domain/packet';

import type { RootState } from '#root//rootState';
import type { ApiResponse } from '@/common/data/parkstory/parkstory';
import { RequestTypeEnum } from '@/common/domain/toast';
import { mergeLists } from '@/common/utils/mergeLists';
import { logException, logTrace, SeverityLevel } from '@/common/utils/logger';
import { setFleets } from '@/fleet/application/fleetsSlice';
import useFleetApi from '@/fleet/data/fleetApi';
import { removeInvitationFromParkingAdminGroup, setParkingAdminGroup } from '@/parking/application/parkingSlice';
import { clearCurrResaClass, clearPendingGroupConfigEnterprise, setCurrResaClass, setPendingGroupConfigEnterprise } from '@/subscription/application/subscriptionSlice';
import { useSubscriptionStore } from '@/subscription/data/subscriptionStore';
import type { Guest } from '@/subscription/domain/resaClass';
import type { Fleet } from '@/fleet/domain/fleet';

const selectParkingAdminsForCurrentParking = createSelector(
    (state: RootState) => state.context.parkingAdmins,
    (state: RootState) => state.context.parking?.id,
    (parkingAdmins, parkingId) =>
        parkingAdmins?.filter(admin =>
            admin.relatesTo?.some(item => item.targetId === parkingId?.toString())
        )
);

export const useGroup = () => {
    const dispatch = useDispatch();
    const store = useStore<RootState>();
    const {
        apiGetGroups,
        apiGetGroupsByRelations,
        apiGetInvitation,
        apiDeletePackets,
        apiConfirmInvitationToGroup,
        apiCreateGroup,
        apiUpdateGroup,
        apiDeleteGroup,
        apiRemoveMemberFromGroup,
        apiCreatePacket,
        apiUpdatePacket,
        apiSetGroupPacket,
        apiInviteMember,
        apiDeleteInvitation,
        apiGetOwnershipGroupsRT,
        apiGetGroup,
        apiGrantAccessToGroup,
        apiGrantReadToGroup,
        apiGrantAccessToPacket,
        apiCreateGroupConfig,
        apiDeleteGroupConfig,
        apiGetQRCodePdf,
        apiReset2FA
    } = useGroupApi();

    const { getReservationClassByClient } = useSubscriptionStore();

    const { apiGetFleets, apiGetFleetsByIds } = useFleetApi();


    const fleets = useSelector((state: RootState) => state.fleets);
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const parkingAdmins = useSelector(selectParkingAdminsForCurrentParking);
    const resaClass = useSelector((state: RootState) => state.context.currentResa);
    const superAdminGroup = useSelector((state: RootState) => state.context.superGroupAdmin);
    const { getAdminById, getById, inheritedGroupBySuper, deleteGroup: deleteGroupInStore, preLoadSuperAdmin } = useGroupStore();

    /*
     * find admin group for the provided groupId. Look for group with ownership grpConfig and relatesTo 
     * 
     */

    const findAdminGroup = async (groupId: string) : Promise<Group | null> => {
        try {
            const adminGrpResp = await getAdminById(groupId);
            if (adminGrpResp?.id) {
                dispatch(addAdminInLib({ group: { ...adminGrpResp, adminOf: groupId }, key: groupId }));
                dispatch(setCurrentAdminGroup(adminGrpResp));
                return adminGrpResp;
            } else {
                throw new Error('Admin group not found');
            }

            /*
             *handleHttpError(adminGrpResp.status);
             *dispatch(setApiResponseStatus(adminGrpResp.status));
             *dispatch(setApiResponseMeta(adminGrpResp.meta as any));
             */
             
            
        } catch (e) {
            logException(e, { groupId });
            return null;

        }
    };
    const findAdminGroupFromStore = async (groupId: string) => {
        try {
            return await getAdminById(groupId);
        } catch (error) {
            logException(error, { groupId });
        }
    };

    /*
     * Assign a fleet to a group by adding a `relatesTo` membership relation (targetModel 'fleet'),
     * mirroring how holdings assign parkings. Persisted through upsertGroup, then the group is
     * re-fetched so the (derived) assigned-fleets list refreshes.
     *
     * IMPORTANT: the backend keys ALL fleet permissions and lookups on the fleet `documentId`
     * (IDM targets are `fleets::<documentId>`), so the relation MUST carry the documentId — never
     * the numeric id. Writing the numeric id means the backend can neither grant group members
     * read access nor resolve the fleet on read.
     */
    const assignFleetToGroup = async (group: Group, fleet: Fleet): Promise<Group | null> => {
        if (!group?.documentId || !fleet.documentId) {
            logTrace('assignFleetToGroup: missing group/fleet documentId — skipping', SeverityLevel.Warning, { groupId: group?.documentId, fleetId: fleet.documentId });
            return null;
        }
        const targetId = fleet.documentId;

        // Recognise an existing relation by either identifier so groups saved before the
        // documentId migration (numeric targetId) aren't linked twice.
        const alreadyLinked = (group.relatesTo ?? []).some(
            rel => rel.targetModel === 'fleet' && (rel.targetId === targetId || rel.targetId === fleet.id?.toString())
        );
        if (alreadyLinked) return group;

        const relatesTo: RelatesTo[] = [
            ...(group.relatesTo ?? []),
            { relation: EnumRelation.MEMBERSHIP, targetId, targetModel: 'fleet' },
        ];
        // Only the relation changes here — strip the packet so upsertGroup doesn't redundantly
        // re-run (and potentially fail) the packet update.
        const updated = await upsertGroup({ group: { ...group, packet: undefined, relatesTo } });
        await apiGrantAccessToGroup(fleet.documentId, group.documentId);
        if (updated?.documentId) await getGroupById(updated.documentId);
        return updated;
    };

    /*
     * Pick the real fleet-admin group when several user-groups own the same fleet via an `ownership`
     * relation — which can happen if two check-then-create paths race and both create the group before
     * either sees the other. This mirrors the backend's deterministic dedupe (see user-group.ts
     * findFleetAdminGroup) so both sides resolve to the same group: dedicated `_subgroupFleetAdmin`
     * prefix → most members → oldest (createdAt) → lowest id.
     */
    const FLEET_ADMIN_PREFIX = '_subgroupFleetAdmin';
    const pickFleetAdminGroup = (candidates: Group[]): Group | null => {
        if (candidates.length <= 1) return candidates[0] ?? null;
        logTrace('findFleetAdminGroup: disambiguating ownership groups for the same fleet', SeverityLevel.Warning, { candidateCount: candidates.length });
        // Returns <0 when `a` should win over `b`, matching the backend's ordering exactly. createdAt
        // is an ISO-8601 string, so a lexical compare is chronological (oldest first).
        const better = (a: Group, b: Group): number => {
            const isAdmin = (g: Group): number => (g.prefix === FLEET_ADMIN_PREFIX ? 1 : 0);
            return (
                isAdmin(b) - isAdmin(a) ||
                (b.members?.length ?? 0) - (a.members?.length ?? 0) ||
                (a.createdAt ?? '').localeCompare(b.createdAt ?? '') ||
                (a.id ?? 0) - (b.id ?? 0)
            );
        };
        return candidates.reduce((best, g) => (better(g, best) < 0 ? g : best), candidates[0]);
    };

    /*
     * Find a FLEET's own admin group: a user-group that owns the fleet via an `ownership` relation
     * (targetId = the fleet documentId), mirroring how a user-group's admin group is discovered.
     * Used when a group's fleet has its OWN admins (useUserGroupAdminAsFleetAdmin = false).
     */
    const findFleetAdminGroup = async (fleetDocumentId: string): Promise<Group | null> => {
        if (!fleetDocumentId) {
            return null;
        }
        try {
            // Step 1: the ownership list query only populates members shallowly (bare refs, no
            // userInfos), and it returns every ownership group so we can dedupe race-created copies.
            const picked = pickFleetAdminGroup(await apiGetOwnershipGroupsRT(fleetDocumentId));
            if (!picked?.documentId) {
                logTrace('[fleet-admin] findFleetAdminGroup resolved (no group)', SeverityLevel.Information, { fleetDocumentId });
                return picked;
            }
            // Step 2: re-fetch the chosen group by documentId so its members come back as full objects
            // with userInfos — same enrichment the classic admin path gets via getAdminById/apiGetGroup.
            const full = (await apiGetGroup(picked.documentId)) ?? null;
            const resolved = full ?? picked;
            logTrace('[fleet-admin] findFleetAdminGroup resolved', SeverityLevel.Information, {
                fleetDocumentId,
                pickedDocumentId: picked.documentId,
                pickedPrefix: picked.prefix ?? undefined,
                enriched: Boolean(full),
                members: resolved?.members?.length ?? 0,
                membersWithUserInfos: resolved?.members?.filter((m) => m.userInfos).length ?? 0,
            });
            return resolved;
        } catch (error) {
            logException(error, { fleetDocumentId });
            return null;
        }
    };

    /*
     * A fleet-admin group is created by the backend the first time a roster is written (or invited
     * into), so only the caller who happened to trigger that write ends up with rights on it, and it
     * carries whatever name the backend assigned. Apply the full fleet-admin group policy every time:
     * idempotent, so safe to call on every write (mirrors the grant/rename contract already used for
     * the group's own admin group in upsertGroup).
     *  - name = "<group name> Fleet Admin" (mirrors the ' Admin' suffix convention below)
     *  - fleet-admin group gets full rights on the fleet itself (IDM grants are a generic ACL — a
     *    fleet's documentId works as a grant target via the same user-groups/:id/grant route, exactly
     *    as assignFleetToGroup already relies on)
     *  - fleet-admin group gets full rights on itself
     *  - the owning group's admin group keeps FULL rights on the fleet-admin group (not narrowed to
     *    READ — that's what lets a group admin delete a pending fleet-admin invite directly, see
     *    useGroupDetailsScreen.handleDeleteFleetAdminInvite) and, new, the fleet-admin group gets READ
     *    back on the admin group
     *  - fleet-admin group gets READ on the user group
     *  - superAdminGroup (Local Admins) gets full rights on the fleet-admin group
     */
    const applyFleetAdminGroupPolicy = async (fleetDocumentId: string, groupDocumentId: string, groupName: string) => {
        if (!fleetDocumentId || !groupDocumentId) return;
        const fleetAdminGroup = await findFleetAdminGroup(fleetDocumentId);
        if (!fleetAdminGroup?.documentId) {
            logTrace('[fleet-admin] no fleet-admin group yet — nothing to apply policy to', SeverityLevel.Information, { fleetDocumentId, groupDocumentId });
            return;
        }

        const desiredName = `${groupName} Fleet Admin`;
        if (fleetAdminGroup.name !== desiredName) {
            await apiUpdateGroup({
                id: fleetAdminGroup.id,
                documentId: fleetAdminGroup.documentId,
                name: desiredName,
                data: undefined,
                adminGroup: undefined
            });
        }

        await apiGrantAccessToGroup(fleetDocumentId, fleetAdminGroup.documentId);
        await apiGrantAccessToGroup(fleetAdminGroup.documentId, fleetAdminGroup.documentId);

        const adminGroup = await findAdminGroupFromStore(groupDocumentId);
        if (adminGroup?.documentId) {
            await apiGrantAccessToGroup(fleetAdminGroup.documentId, adminGroup.documentId);
            await apiGrantReadToGroup(adminGroup.documentId, fleetAdminGroup.documentId);
        } else {
            logTrace('[fleet-admin] no admin group resolved — skipping admin-group grants', SeverityLevel.Warning, { groupDocumentId, fleetAdminGroupDocumentId: fleetAdminGroup.documentId });
        }

        await apiGrantReadToGroup(groupDocumentId, fleetAdminGroup.documentId);

        if (superAdminGroup?.documentId) {
            await apiGrantAccessToGroup(fleetAdminGroup.documentId, superAdminGroup.documentId);
        }
    };

    /*
     * Lazily create the fleet's admin group if `findFleetAdminGroup` finds none yet. There is no
     * dedicated backend route for this — a fleet-admin group is an ordinary user-group, created the
     * same way a group's own admin group is (`_subgroupAdmin` in upsertGroup), just ownership-linked
     * to the fleet instead of to a group. Idempotent against races: a caller who loses the create race
     * still resolves the same group afterwards via findFleetAdminGroup's pickFleetAdminGroup dedupe.
     */
    const getOrCreateFleetAdminGroup = async (fleetDocumentId: string, groupName: string): Promise<Group | null> => {
        const existing = await findFleetAdminGroup(fleetDocumentId);
        if (existing?.documentId) return existing;

        try {
            const created = await apiCreateGroup({
                defaultDuration: 0,
                name: `${groupName} Fleet Admin`,
                prefix: FLEET_ADMIN_PREFIX,
                relatesTo: [{
                    relation: EnumRelation.OWNERSHIP,
                    targetId: fleetDocumentId,
                    targetModel: 'fleet',
                }],
                data: undefined,
                adminGroup: undefined,
            });
            return created.data?.documentId ? created.data : null;
        } catch (error) {
            logException(error, { fleetDocumentId });
            return null;
        }
    };

    // Replace the fleet-admin group's members with exactly `memberRefs`, wholesale — creating the
    // group on the first write if it doesn't exist yet (see getOrCreateFleetAdminGroup). An empty
    // `memberRefs` empties the roster, which is a valid state (group admins can re-add members later).
    const replaceFleetAdmins = async (
        fleetDocumentId: string,
        memberRefs: string[],
        groupName: string,
    ): Promise<Group | null> => {
        const fleetAdminGroup = await getOrCreateFleetAdminGroup(fleetDocumentId, groupName);
        if (!fleetAdminGroup?.documentId) return null;
        try {
            const response = await apiUpdateGroup({
                ...fleetAdminGroup,
                members: memberRefs.map((ref): Member => ({ user: ref })),
            });
            handleHttpError(response.status);
            dispatch(toggleToast({ ...response.status, requestType: RequestTypeEnum.editMember }));
            dispatch(setApiResponseStatus(response.status));
            return response.data ?? null;
        } catch (error: any) {
            if (error?.status) dispatch(toggleToast({ ...error.status, requestType: RequestTypeEnum.editMember }));
            return null;
        }
    };

    // Invite fleet admins by email — the roster-replace call above only accepts refs of users who
    // already exist, so a genuinely new person needs a proper email invite. Same lazy-creation
    // contract as replaceFleetAdmins.
    const inviteFleetAdmins = async (
        fleetDocumentId: string,
        groupName: string,
        invitation: PostInvitation,
    ): Promise<Group | null> => {
        const fleetAdminGroup = await getOrCreateFleetAdminGroup(fleetDocumentId, groupName);
        if (!fleetAdminGroup?.documentId) return null;
        try {
            const response = await apiInviteMember(fleetAdminGroup.documentId, invitation);
            handleHttpError(response.status);
            dispatch(toggleToast({ ...response.status, requestType: RequestTypeEnum.inviteMember }));
            dispatch(setApiResponseStatus(response.status));
            return response.data ?? null;
        } catch (error: any) {
            if (error?.status) dispatch(toggleToast({ ...error.status, requestType: RequestTypeEnum.inviteMember }));
            return null;
        }
    };

    // TODO get packets and and modify populate of groupRefs by id
    const getGroupsAdmin = async () => {
        const responseGroups = await preLoadSuperAdmin();
        return responseGroups;
    };

    // TODO for display in dashboard
    /*
     *    const getInheritedGroupsAdmin = async () => {
     *     const responseGroups = await apiGetGroupsByPrefix('_subgroupAdmin');
     *     return responseGroups.data;
     *    };
     */

    // TODO get packets and and modify populate of groupRefs by id
    const getParkingAdmins = async () => {
        const responseGroups = await apiGetGroupsByRelations('parking', 'ownership');

        return responseGroups.data;
    };

    // TODO get packets and and modify populate of groupRefs by id
    const getGroups = async () => {
        try {
            const responseGroups = await apiGetGroups();
            handleHttpError(responseGroups.status);
            dispatch(setApiResponseStatus(responseGroups.status));
            dispatch(setApiResponseMeta(responseGroups.meta as any));

            // dispatch(setGroups(responseGroups.data));

            if (fleets.list.length <= 0) {
                try {
                    const responseFleets = await apiGetFleets();
                    handleHttpError(responseFleets.status);
                    dispatch(setApiResponseStatus(responseFleets.status));
                    dispatch(setApiResponseMeta(responseFleets.meta as any));
                    dispatch(setFleets(responseFleets.data));
                } catch (e) {
                    logException(e);
                }
            }

            const groups = responseGroups.data;

            dispatch(setGroups(groups));
            return groups;
        } catch (error: any) {
            dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.getGroup}));
            throw error;
        }
    };

    const getGroupById = async (documentId: string, force: boolean = false) => {
        if (!documentId) return;
        try {
            /*
             *const response = await apiGetGroup(documentId);
             *handleHttpError(response.status);
             */
            // `force` bypasses the store cache — required after a mutation (e.g. a fleet was just
            // assigned) so the refreshed group reflects the new relatesTo / assignedFleets.
            const grp = await getById(documentId, force);
            if (!grp?.documentId) return { data: grp };
            dispatch(clearPendingGroup());
            dispatch(setGroup(grp));
            dispatch(setPendingGroup(grp));

            /*
             *dispatch(setApiResponseStatus(response.status));
             *dispatch(setApiResponseMeta(response.meta as any));
             */
            const convertedGroupUsers = grp.members;
            dispatch(setMembers(convertedGroupUsers as Member[]));
            return { data: grp };
        } catch (error: any) {
            if (error?.status) {
                dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.getGroup}));
            }
        }
    };

    // Filter a group list based on if user is an admin or a member of the group
    const filterGroupsForUserRole = async (groups: Group[], userRef: string) : Promise<Group[] | []> => {
        try {

            // list groups where user is member
            const list = groups.filter((group) =>
                group.members?.some(member => member.user === userRef)
            );

            // list admin grps
            const adminGroups: Group[] = [];
            for (const group of groups) {
                if (group?.documentId ?? group?.id?.toString()) {
                    const newGrp: Group = await findAdminGroup(group?.documentId ?? group?.id?.toString());
                    adminGroups.push(newGrp);
                }
            }

            if (adminGroups && adminGroups.length > 0) {
            // list admin grps where user is member
                const myAdminGrps: Group[] = adminGroups.filter(group =>
                    group?.members?.some(member => member.user === userRef)
                );

                // List group where user is a manager
                const grpsIManage = groups.filter(group =>
                    myAdminGrps.some(grp =>
                        grp?.relatesTo && grp?.relatesTo[0]?.targetId === (group?.documentId ?? group?.id?.toString())
                    )
                );

                const mergedList = mergeLists(list, grpsIManage);
                return mergedList;
            } else {
                return list;
            }
        } catch (error) {
            logException(error);
            return [];
        }
    };

    const reset2FA = async (
        group: Group,
        userRef: string 
    ) => {
        if (group.documentId) {
            try {
                const response = await apiReset2FA(group.documentId, userRef);

                dispatch(toggleToast({...response.status, requestType: RequestTypeEnum.editMember}));
                dispatch(setApiResponseStatus(response.status));
                return response.status.code === 200;

            } catch (error: any) {
                dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.editMember, text: error.response?.data?.error?.message }));
                return false;
            }
            
        }
        return false;

    };


    const upsertGroup = async (
        pendingGroup: PendingGroupSlice,
        superGroup?: Group,
        superAdminGroup?: Group,
        rmPacket?: Packet,
        fleet?: Fleet
    ): Promise<Group | null> => {
        try {
            const mutableGroup = { ...pendingGroup.group };
            let currentAdminGroup = (pendingGroup.group.documentId) ? findAdminGroupFromStore(pendingGroup.group.documentId) : null;

            // const packetHandler = buildPacket(mutableGroup, pendingGroup);

            let response: ApiResponse<Group | null>;
            if (typeof mutableGroup.id !== 'undefined') {

                // -------UPDATE------
                try {
                    // If the group already exists, update it
                    response = await apiUpdateGroup({
                        documentId: mutableGroup.documentId,
                        cover: mutableGroup.cover,
                        defaultDuration: 0,
                        id: mutableGroup.id,
                        name: mutableGroup.name,
                        packet: mutableGroup.packet,
                        prefix: mutableGroup.prefix ?? '_subgroup',
                        relatesTo: mutableGroup.relatesTo,
                        data: undefined,
                        adminGroup: undefined
                    });

                    if (response.data?.documentId) await getById(response.data.documentId, true);

                    // Handle admin grp name update
                    const admGrp = await findAdminGroupFromStore(mutableGroup.documentId ?? mutableGroup.id?.toString() ?? '');
                    if (admGrp?.documentId && mutableGroup.name !== admGrp?.name.slice(0, -6)) {
                        await apiUpdateGroup({
                            id: admGrp.id,
                            documentId: admGrp.documentId,
                            name: mutableGroup.name + ' Admin',
                            data: undefined,
                            adminGroup: undefined
                        });
                        if (admGrp?.documentId) await getById(admGrp.documentId, true);
                    }

                    // Handle fleet-admin grp name/grants update — keyed through the group's own fleet,
                    // since a fleet-admin group has no direct link back to the group it administers.
                    const ownFleetIds = fleetRelationTargetIds(mutableGroup, EnumRelation.MEMBERSHIP);
                    if (ownFleetIds.length > 0 && mutableGroup.documentId) {
                        const fleetsResponse = await apiGetFleetsByIds(ownFleetIds);
                        const fleetDocumentId = fleetsResponse.data[0]?.documentId;
                        if (fleetDocumentId) await applyFleetAdminGroupPolicy(fleetDocumentId, mutableGroup.documentId, mutableGroup.name);
                    }
                    response.status = { ...response.status, requestType: RequestTypeEnum.updateGroup};
                } catch (error: any) {
                    dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.updateGroup, text: error.response?.data?.error?.message }));
                    return null;
                }
            } else {
                /*
                 * ----- CREATE -----
                 * If the group is new, create it
                 */
                if (superGroup?.id) {
                    // If the group is a subgroup, creates inheritance relation with relatesTo
                    response = await apiCreateGroup({
                        cover: mutableGroup.cover,
                        defaultDuration: 0,
                        name: mutableGroup.name,
                        prefix: '_subgroup',
                        relatesTo: [{
                            relation: EnumRelation.INHERITANCE,
                            targetId: superGroup?.documentId ?? superGroup?.id?.toString(),
                            targetModel: 'userGroup',
                        }],
                        data: undefined,
                        adminGroup: undefined
                    });

                    // if there's no superGroup and the group doesn't already exist, create group with no relatesTo
                } else {
                    response = await apiCreateGroup({
                        cover: mutableGroup.cover,
                        defaultDuration: 0,
                        name: mutableGroup.name,
                        prefix: '_group',
                        data: undefined,
                        adminGroup: undefined
                    });
                }
                if (response.status.code !== 200 || !response.data?.documentId) {
                    throw Error;
                }

                /*
                 * Create the admin group for the group, with relatesTo ownership
                 * Note: relatesTo replaces groupconfig of type  OF - ownership and inheritance 
                 */
                const adminGroupResponse = await apiCreateGroup({
                    defaultDuration: 0,
                    name: mutableGroup.name + ' Admin',
                    prefix: '_subgroupAdmin',
                    relatesTo: [{
                        relation: EnumRelation.OWNERSHIP,
                        targetId: response.data?.documentId ?? response.data?.id?.toString(),
                        targetModel: 'userGroup'
                    }],
                    data: undefined,
                    adminGroup: undefined
                });

                // Create the groupConfigs for inheritance and ownership
                if (superGroup?.id && response.data?.documentId) {
                    await apiCreateGroupConfig({
                        groupConfig: {
                            __component: 'user.of',
                            relation: 'inheritance',
                            targetId: superGroup.id.toString(),
                            targetModel: 'group',
                        } as GroupConfigOf,
                        userGroups: [response.data],
                        data: undefined,
                        status: function (): unknown {
                            throw new Error('Function not implemented.');
                        },
                        meta: undefined
                    },
                    response.data.documentId
                    );
                }

                if (response.data.documentId && adminGroupResponse.data?.documentId) {
                    await apiCreateGroupConfig({
                        groupConfig: {
                            __component: 'user.of',
                            relation: 'ownership',
                            targetId: response.data.documentId,
                            targetModel: 'group',
                        } as GroupConfigOf,
                        userGroups: [adminGroupResponse.data],
                        data: undefined,
                        status: function (): unknown {
                            throw new Error('Function not implemented.');
                        },
                        meta: undefined
                    },
                    adminGroupResponse.data.documentId
                    );
                }

                /* Link an already-existing fleet to the group (by name) via the group's
                 * relatesTo field. Creating a brand-new fleet + assigning it goes through
                 * assignFleetToGroup (called from the fleet-creation flow), not here.
                 */
                if (fleet?.name && response.data.documentId) {
                    const fleetResponse = await apiGetFleets();
                    const existingFleet = fleetResponse.data.find(f => f.name === fleet?.name);
                    if (existingFleet?.documentId && response.data?.documentId) {
                        await assignFleetToGroup(response.data, existingFleet);
                        await apiGrantAccessToGroup(existingFleet.documentId, response.data.documentId);
                    }
                }

                if (response.data.documentId && adminGroupResponse.data?.documentId) {
                    currentAdminGroup = adminGroupResponse.data;

                    // admin to group
                    await apiGrantAccessToGroup(response.data.documentId, adminGroupResponse.data.documentId);

                    // group readto supergroup (should be redundent)
                    if (superGroup?.documentId) await apiGrantReadToGroup(response.data.documentId, superGroup.documentId);

                    // group readto superadmingroup (should be redundent)
                    if (superAdminGroup?.documentId) await apiGrantReadToGroup(response.data.documentId, superAdminGroup.documentId);

                    // superAdmin to group
                    if (superAdminGroup?.documentId) await apiGrantAccessToGroup(response.data.documentId, superAdminGroup.documentId);

                    // group readto admingroup
                    await apiGrantReadToGroup(adminGroupResponse.data.documentId, response.data.documentId);

                    // admin to admin
                    await apiGrantAccessToGroup(adminGroupResponse.data.documentId, adminGroupResponse.data.documentId);

                    // superadmin to admin
                    if (superAdminGroup?.documentId) await apiGrantAccessToGroup(adminGroupResponse.data.documentId, superAdminGroup.documentId);
                    
                    if (parkingAdmins) {
                        // all parking admin to group && admin
                        parkingAdmins.forEach(async (admns) => {
                            if (admns?.documentId && response?.data?.documentId) {
                                await apiGrantAccessToGroup(response.data.documentId, admns.documentId);
                                if (adminGroupResponse?.data?.documentId) await apiGrantAccessToGroup(adminGroupResponse.data.documentId, admns.documentId);
                            }
                        });
                    }
                    if (currentUser?.ref) await apiRemoveMemberFromGroup(response.data.documentId, currentUser?.ref);
                    if (currentUser?.ref && adminGroupResponse.data.documentId) await apiRemoveMemberFromGroup(adminGroupResponse.data.documentId, currentUser?.ref);
                    if (response.data?.documentId) await getById(response.data.documentId, true);
                    if (adminGroupResponse.data?.documentId) await getById(adminGroupResponse.data.documentId, true);

                }
            }

            // Update packetHandler.packet if needed
            if (mutableGroup.packet && response?.data?.documentId) {
                // Merge full packet-config defaults so a complete config.features /
                // config.payment is always sent, even from partial UI edits.
                const mutablePacket = {
                    ...mutableGroup.packet,
                    config: {
                        features: { ...defaultPacketConfig.features, ...mutableGroup.packet.config?.features },
                        payment: { ...defaultPacketConfig.payment, ...mutableGroup.packet.config?.payment },
                    },
                };
                /*
                 * The create/update branch hinges on the packet keeping its identity through the form.
                 * A missing `id` on an EXISTING group silently creates a duplicate packet, so log which
                 * branch is taken — it is the first thing to check when config toggles don't persist.
                 */
                logTrace('[packet] save', SeverityLevel.Information, {
                    branch: mutablePacket.id ? 'update' : 'create',
                    packetId: mutablePacket.id ?? undefined,
                    packetDocumentId: mutablePacket.documentId ?? undefined,
                    groupDocumentId: response.data.documentId,
                });
                /*
                 * The fleet-admin roster is NOT reconciled here. Saving the packet no longer has any
                 * fleet-admin side effect (the backend's packet-side reconcile was inert and is gone);
                 * GroupPropertiesScreen drives it explicitly via `replaceFleetAdmins` when the
                 * useUserGroupAdminAsFleetAdmin flag actually changes.
                 */
                try {
                    const responsePacket = mutablePacket.id
                        ? await apiUpdatePacket({...mutablePacket, resaClassRef: resaClass?.documentId?.toString() ?? '' })
                        : await apiCreatePacket({...mutablePacket, resaClassRef: resaClass?.documentId?.toString() ?? '', reservedNumber: mutablePacket.reservedNumber ?? 0});
                    if (!mutablePacket.id) {
                        if (responsePacket.data.documentId && response?.data?.documentId) {
                            await apiSetGroupPacket(response.data.documentId, responsePacket.data.documentId);
                        }
                        await apiGrantAccessToPacket(responsePacket.data.documentId ?? '', response?.data?.documentId ?? '');
                        
                        if (superAdminGroup?.documentId) {
                            await apiGrantAccessToPacket(responsePacket.data.documentId ?? '', superAdminGroup.documentId);
                        }
                        if (currentAdminGroup?.documentId) await apiGrantAccessToPacket(responsePacket.data.documentId ?? '', currentAdminGroup.documentId);
                    }
                    if (response.data.documentId ?? mutableGroup.documentId) await getById(response.data.documentId ?? mutableGroup.documentId ?? '', true);
                    handleHttpError(responsePacket.status);
                } catch (error: any) {
                    dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.updateGroup, text: error.response?.data?.error?.message }));
                    const err = new Error(error.response?.data?.error?.message || error.message);
                    Object.assign(err, { createdGrpId: response?.data?.id ?? null });
                    throw err;
                }
            } else if (rmPacket?.documentId) {
                await apiDeletePackets(rmPacket.documentId);
            }
           
            // Dispatch actions
            dispatch(toggleToast({...response?.status, requestType: RequestTypeEnum.createGroup}));
            dispatch(setApiResponseStatus(response?.status));
            dispatch(clearPendingGroup());

            // Return createdGorup
            return response?.data ?? null;
        } catch (error: any) {
            logException(error, { groupId: pendingGroup.group.documentId });
            if (error.message == 'Not enough slots available') {
                throw error;
            }
            if ((error.message.includes('Cannot') && error.message.includes('revoke') && error.message.includes('reservation') && error.message.includes('in progress')) || (error.message.includes('Cannot') && error.message.includes('payment') && error.message.includes('in progress'))) {
                throw error;
            }

            // Handle error and return null
            dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.createGroup, text: error.response?.data?.error?.message }));
           
            return null;
        }
    };
    const updatePendingGroupConfigHoldingPropsCont = (field: string, value: any) => {
        dispatch(updatePendingGroupConfigHoldingProps({ field, value }));
    }; 

    const clearPendingGroupCont = async () => {
        dispatch(clearPendingGroup());
    };

    const clearGroupCont = async () => {
        dispatch(clearGroup());
    };

    const setPendingGroupCont = (pendingGroup: Group) => {
        dispatch(setPendingGroup(pendingGroup));
    };
    const setPendingGroupAdmin = (pendingGroup: Group) => {
        dispatch(setCurrentAdminGroup(pendingGroup));
    };

    const updatePendingGroupPropsCont = (attributesToUpdate: {
        key: string;
        value: unknown;
    }) => {
        dispatch(updatePendingGroupProps(attributesToUpdate));
    };
    const getSubGroupsByInheritance = async (groupId: string, updateGroupsInStore: boolean = true) : Promise<Group[] | null> => {
        try {
            const groups = await inheritedGroupBySuper(groupId);
            // The store list belongs to the selected subscriber: a response for another one (the user
            // switched while it was in flight) must not overwrite it. Read the state now, not the
            // value captured when this hook rendered.
            if (updateGroupsInStore && store.getState().context.superGroup?.documentId === groupId) {
                dispatch(setGroups(groups));
            }

            return groups;
        } catch (error: any) {
            logException(error, { groupId });
            if (error?.status) {
                dispatch(toggleToast(error?.status));
            }
            return null;
        }
    };

    // Get guests from sub groups of given company id
    const getSubGroupsGuests = async (superGrpId: string) => {
        const subGroups : Group[] | null = await getSubGroupsByInheritance(superGrpId, false);
        const allGuests : Guest[] = [];

        if (subGroups) {
            subGroups.forEach(subgroup => {
                if (subgroup.guests && subgroup.guests.length > 0) {
                    subgroup?.guests?.forEach(guest => {
                        allGuests.push(guest as unknown as Guest);
                    });
                }
            });
        }
        return allGuests;
    };

    // Delete grp and its grpconfig, grp admin, subgroups
    const deleteUserGrp = async (group: pGroup, packetsId?: string) => {
        try {
            if (group.documentId !== undefined) {

                // Delete the user group
                const response = await apiDeleteGroup(group.documentId);
                await deleteGroupInStore(group.documentId);
                dispatch(removeGroup(group.documentId));

                // Recursively delete the subgroups 
                const subgroups: Group[] | null = await getSubGroupsByInheritance(group.documentId, false); // get subgroups without updating groups in store
                if (subgroups?.length && subgroups.length > 0) {
                    for (const group of subgroups) {
                        if (group.documentId !== null && typeof group.documentId !== 'undefined') {
                            await deleteUserGrp(group, packetsId);
                            await deleteGroupInStore(group.documentId);
                        }
                    }
                }

                // Delete the adminGroup (if it hasn't already been deleted as a subgroup)
                let admingroup = group.admingroup ?? null;
                if (group.admingroup?.documentId === undefined && group.documentId) {
                    const findAdminGroupResp = await findAdminGroup(group.documentId);
                    if (findAdminGroupResp) {
                        admingroup = findAdminGroupResp;
                    }

                    if (admingroup?.documentId) {
                        await apiDeleteGroup(admingroup.documentId);
                        await deleteGroupInStore(admingroup.documentId);
                        dispatch(removeGroup(admingroup.documentId));
                    }
                }

                // Delete the groupConfig, if any — groups whose relations are expressed via
                // `relatesTo` instead (see assignFleetToGroup) have none, so this is optional.
                const groupConfigId = group.groupConfigs?.[0]?.documentId;
                if (groupConfigId) {
                    await apiDeleteGroupConfig(groupConfigId);
                }

                if (typeof packetsId !== 'undefined') {
                    await apiDeletePackets(packetsId);
                }

                handleHttpError(response.status);
                dispatch(toggleToast({...response.status, requestType: RequestTypeEnum.deleteGroup}));
                dispatch(setApiResponseStatus(response.status));
                const newGroups = [] as Group[]; // await getGroups(); // TODO remove, we wont need that when API will return updated group list
                return {
                    data: newGroups,
                    meta: response.meta,
                    status: response.status,
                };
            } else {
                throw new Error('No group documentId found');
            }
        } catch (error: any) {
            logException(error, { groupId: group.documentId });
            // Surface the failure even when it didn't come from the API (e.g. the guard above) and
            // so has no `.status` — a caught error with no toast reads as "nothing happened".
            dispatch(toggleToast({
                code: error?.status?.code ?? 400,
                requestType: RequestTypeEnum.deleteGroup,
                text: error?.status?.text ?? error?.message ?? 'error',
            }));
        }
    };

    // Delete only the group
    const deleteGroup = async (documentId: string) => {
        try {
            const response = await apiDeleteGroup(documentId);
            
            handleHttpError(response.status);
            dispatch(toggleToast({...response.status, requestType: RequestTypeEnum.deleteGroup}));
            dispatch(setApiResponseStatus(response.status));
            await deleteGroupInStore(documentId);

            const newGroups = [] as Group[]; // await getGroups(); // TODO remove, we wont need that we API will return updated group list
            return {
                data: newGroups,
                meta: response.meta,
                status: response.status,
            };
        } catch (error: any) {
            if (error?.status) {
                dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.deleteGroup}));
            }
        }
    };

    const getInvitationById = async (documentId: string) => {
        try {
            const response = await apiGetInvitation(documentId);
            handleHttpError(response.status);
            dispatch(setApiResponseStatus(response.status));
            return response.data;
        } catch (error: any) {
            if (error?.status) {
                dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.getInvitation}));
            }
            throw error;
        }
    };

    const confirmInvitationToGroup = async (
        code: number,
        invitation: string,
        groupDocumentId: string
    ) => {
        try {
            const response = await apiConfirmInvitationToGroup(
                code,
                invitation,
                groupDocumentId
            );
            handleHttpError(response.status);
            dispatch(toggleToast({...response.status, requestType: RequestTypeEnum.confirmInvitationToGroup}));
            dispatch(setApiResponseStatus(response.status));
            return response.status;
        } catch (error: any) {
            if (error?.status) {
                dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.confirmInvitationToGroup}));
            }
        }
    };


    const inviteMember = async (groupDocumentId: string, invitation: PostInvitation) => {
        try {
            const response = await apiInviteMember(groupDocumentId, invitation);
            handleHttpError(response.status);
            dispatch(toggleToast({...response.status, requestType: RequestTypeEnum.inviteMember}));
            dispatch(setApiResponseStatus(response.status));
            return response;
        } catch (error: any) {
            logException(error, { groupId: groupDocumentId });
            if (error?.status) {
                dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.inviteMember}));
            }
        }
    };

    const removeMemberFromGroup = async (groupDocumentId: string, memberId: string, dispatchAction?:any, disableToast = false) => {
        try {
            const response = await apiRemoveMemberFromGroup(groupDocumentId, memberId);
            handleHttpError(response.status);
            if (!disableToast) dispatch(toggleToast({...response.status, requestType: RequestTypeEnum.removeMemberFromGroup}));
            dispatch(setApiResponseStatus(response.status));
            dispatch(removeMember(Number(memberId)));
            const updgroup = await getById(groupDocumentId, true);
            if (dispatchAction) dispatch(dispatchAction(updgroup));
            return response;
        } catch (error: any) {
            if (error?.status) {
                dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.removeMemberFromGroup}));
            }
        }
    };

    const deleteInvitation = async (
        groupDocumentId: string,
        invitationId: string,
        index: number,
        type?: 'companyAdmin' | 'parkingAdmin' | 'userGroup'
    ) => {
        try {
            const response = await apiDeleteInvitation(groupDocumentId, invitationId);
            handleHttpError(response.status);
            dispatch(toggleToast({...response.status, requestType: RequestTypeEnum.deleteInvitation}));
            dispatch(setApiResponseStatus(response.status));
            
            const updatedgroup = await getById(groupDocumentId, true);

            if (response.status.code == 200) {
                switch (type) {
                    case 'companyAdmin':
                        dispatch(removeInvitationFromCompanyAdminGroup(index)); // remove invit from state.company.companyAdminGroup
                        if (updatedgroup) dispatch(setCompanyAdminGroup(updatedgroup));

                        break;
                    case 'parkingAdmin':
                        dispatch(removeInvitationFromParkingAdminGroup(index));// remove inv from state.parking.parkingAdminGroup
                        if (updatedgroup) dispatch(setParkingAdminGroup(updatedgroup));
                        break;
                    default:
                        dispatch(removeInvitation(index));
                        break;
                }
                return response;
            }
        } catch (error: any) {
            if (error?.status) {
                dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.deleteInvitation}));
            }
        }
    };


    const getGroupsByIds = async (ids: string[]) => {
        const groups: Group[] = [];
        await Promise.all(ids.map(async (id) => {
            const group: Group | null = (await getGroupById(id))?.data ?? null;
            if (group) {
                groups.push(group);
            }
        }));
        return groups;
    };

    const getQRCodePdf = async (groupId: string, guestId: string) : Promise<Blob | null> => {
        try {
            const response = await apiGetQRCodePdf(groupId, guestId);
            const pdfBlob = new Blob([response.data], { type: 'application/pdf'});
            return pdfBlob;
        } catch (error) {
            logException(error, { groupId, guestId });
            return null;
        }
    };


    /*
     * Takes client id and set group config and resa class in state
     * 
     * TODO rename
     */
    const setGrpConfResaClass = async (groupId: string) => {
        const group = await getById(groupId);
        const groupConfig = group?.groupConfigs?.[0]?.groupConfig ?? null;
        if (groupConfig) dispatch(setPendingGroupConfigEnterprise(groupConfig as GroupConfigEnterprise));
        const resaClass = (await getReservationClassByClient(group?.documentId ?? groupId));
        if (resaClass) dispatch(setCurrResaClass(resaClass));
        return { groupConfig: groupConfig, resaClass: resaClass };
    };
    const clearGrpConfResaClass = async () => {
        dispatch(clearCurrResaClass());
        dispatch(clearPendingGroupConfigEnterprise());
    };

    // Remove a fleet's membership relation from a group. Matches by either identifier so relations
    // stored with a legacy numeric targetId are removed too.
    const unassignFleetFromGroup = async (group: Group, fleet: Fleet): Promise<Group | null> => {
        if (!group?.documentId) return null;

        const relatesTo = (group.relatesTo ?? []).filter(
            rel => !(rel.targetModel === 'fleet' && (rel.targetId === fleet.documentId || rel.targetId === fleet.id?.toString()))
        );
        // Only the relation changes here — strip the packet so upsertGroup doesn't redundantly
        // re-run (and potentially fail) the packet update.
        const updated = await upsertGroup({ group: { ...group, packet: undefined, relatesTo } });
        if (updated?.documentId) await getGroupById(updated.documentId);
        return updated;
    };

    return {
        assignFleetToGroup, unassignFleetFromGroup, findFleetAdminGroup, applyFleetAdminGroupPolicy, replaceFleetAdmins, inviteFleetAdmins,
        clearGrpConfResaClass, updatePendingGroupConfigHoldingPropsCont, clearGroupCont, clearPendingGroupCont, confirmInvitationToGroup, deleteGroup, deleteInvitation, deleteUserGrp, findAdminGroup, findAdminGroupFromStore, getGroupById, getGroups, filterGroupsForUserRole, getGroupsAdmin, getGroupsByIds, getInvitationById,
        getParkingAdmins, getQRCodePdf, getSubGroupsByInheritance, getSubGroupsGuests, inviteMember, removeMemberFromGroup, reset2FA, setGrpConfResaClass, setPendingGroupAdmin, setPendingGroupCont, updatePendingGroupPropsCont, upsertGroup
    };
};
